import { useEffect, useState } from 'react';
import type { StorefrontCatalog } from '../../shared/types/api';
import { ErrorNotice } from '../components/ErrorNotice';
import { LoadingTrail } from '../components/LoadingTrail';
import { TrailCompassArtwork } from '../components/TrailCompassArtwork';
import { ProductCard } from '../features/storefront/ProductCard';
import { StorefrontHeader } from '../features/storefront/StorefrontHeader';
import { api } from '../lib/api';
import { SIMULATED_TRAIL_DISCLOSURE } from '../lib/simulatedTrailDisclosure';

interface StorefrontPageProps {
  navigate: (path: string) => void;
  shopOnly?: boolean;
}

export function StorefrontHero({ navigate }: { navigate: (path: string) => void }) {
  return (
    <section className="store-hero">
      <div>
        <p className="store-kicker">A bracelet. A trail. A new discovery every day.</p>
        <h1>Follow a wild story—one new step at a time.</h1>
        <p>
          Each Wild Trails bracelet pairs with an adult-managed digital animal experience, featuring
          simulated educational tracking, daily stories, facts, and quizzes.
        </p>
        <div className="store-hero-actions">
          <button className="store-button" type="button" onClick={() => navigate('/shop')}>
            Explore bracelets
          </button>
          <button className="store-text-button" type="button" onClick={() => navigate('/account')}>
            Already have a bracelet? Set up or sign in to the grown-up area
          </button>
        </div>
        <p className="store-plain-language">
          One-time purchase. No subscription. {SIMULATED_TRAIL_DISCLOSURE}
        </p>
      </div>
      <div className="store-hero-card" aria-label="A preview of the Wild Trails experience">
        <TrailCompassArtwork compact />
        <p>Today’s trail</p>
        <strong>Ice edge → open water</strong>
        <small>Simulated educational journey</small>
      </div>
    </section>
  );
}

export function StorefrontPage({ navigate, shopOnly = false }: StorefrontPageProps) {
  const [catalog, setCatalog] = useState<StorefrontCatalog | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .listStorefrontProducts()
      .then(setCatalog)
      .catch((reason: unknown) =>
        setError(
          reason instanceof Error ? reason.message : 'The bracelet collection could not load.',
        ),
      );
  }, []);

  if (!catalog && !error) return <LoadingTrail message="Opening the bracelet collection…" />;

  return (
    <div className="store-shell">
      <StorefrontHeader navigate={navigate} />
      <main>
        {!shopOnly && (
          <>
            <StorefrontHero navigate={navigate} />

            <section className="store-steps" id="how-it-works">
              <div className="store-section-heading">
                <p className="store-kicker">How it works</p>
                <h2>A small keepsake opens a big learning world.</h2>
              </div>
              <ol>
                <li>
                  <span>1</span>
                  <strong>Choose a bracelet</strong>
                  <p>Pick one of four animal experiences.</p>
                </li>
                <li>
                  <span>2</span>
                  <strong>An adult sets it up</strong>
                  <p>Use the standard instruction card included in the package.</p>
                </li>
                <li>
                  <span>3</span>
                  <strong>Follow the adventure</strong>
                  <p>Explore simulated trails and daily learning together.</p>
                </li>
              </ol>
            </section>
          </>
        )}

        <section className="store-products" id="bracelets">
          <div className="store-section-heading">
            <p className="store-kicker">Four animal experiences</p>
            <h1>
              {shopOnly
                ? 'Choose your Wild Trails bracelet.'
                : 'Which trail will your family follow?'}
            </h1>
            <p>
              Each bracelet includes access instructions for an adult-managed learning experience.
            </p>
          </div>
          {error && <ErrorNotice message={error} />}
          {catalog?.products.length ? (
            <div className="store-product-grid">
              {catalog.products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onOpen={() => navigate(`/products/${product.slug}`)}
                />
              ))}
            </div>
          ) : (
            <div className="store-empty-card">
              <h3>The collection is being prepared.</h3>
              <p>Reviewed product details will appear here when the production catalog is ready.</p>
            </div>
          )}
        </section>

        {!shopOnly && (
          <>
            <section className="store-experience">
              <div>
                <p className="store-kicker">Inside the experience</p>
                <h2>Maps that spark questions. Stories that reward curiosity.</h2>
                <p>
                  Families can explore a 15-day trail, read a sourced animal fact and story, try a
                  three-question quiz, and play short learning activities.
                </p>
              </div>
              <div className="experience-preview" aria-hidden="true">
                <span className="preview-route">•••⌁•••⌁•••</span>
                <div>
                  <strong>3 / 3</strong>
                  <small>Quiz complete</small>
                </div>
              </div>
            </section>

            <section className="store-parent-note">
              <div>
                <p className="store-kicker">For parents and caregivers</p>
                <h2>Designed for shared, adult-managed exploration.</h2>
              </div>
              <ul>
                <li>An adult creates and manages the account.</li>
                <li>{SIMULATED_TRAIL_DISCLOSURE}</li>
                <li>Access instructions stay on the printed card included with each bracelet.</li>
                <li>No subscriptions, advertising, or third-party analytics.</li>
              </ul>
            </section>

            <section className="store-faq">
              <div className="store-section-heading">
                <p className="store-kicker">Questions, answered</p>
                <h2>Good to know before you choose.</h2>
              </div>
              <details>
                <summary>Is the animal’s location real?</summary>
                <p>
                  No. {SIMULATED_TRAIL_DISCLOSURE} Each trail is generated using species-appropriate
                  movement rules.
                </p>
              </details>
              <details>
                <summary>Is there a subscription?</summary>
                <p>
                  No. The catalog describes a one-time bracelet purchase and does not include a
                  recurring plan.
                </p>
              </details>
              <details>
                <summary>How does a child get access?</summary>
                <p>
                  A parent or caregiver follows the standard printed instruction card included with
                  the bracelet and manages the account.
                </p>
              </details>
            </section>

            <section className="store-final-cta">
              <h2>Ready to find your trail?</h2>
              <p>Meet the four animal experiences and choose the one that fits your explorer.</p>
              <button className="store-button" type="button" onClick={() => navigate('/shop')}>
                See the collection
              </button>
            </section>
          </>
        )}
      </main>
      <StoreFooter navigate={navigate} />
    </div>
  );
}

export function StoreFooter({ navigate }: { navigate: (path: string) => void }) {
  return (
    <footer className="store-footer">
      <div>
        <strong>Wild Trails Kids</strong>
        <p>Simulated animal journeys for curious families. {SIMULATED_TRAIL_DISCLOSURE}</p>
      </div>
      <nav aria-label="Policies">
        {['privacy', 'terms', 'tracking-disclosure', 'parent-notice', 'account-deletion'].map(
          (page) => (
            <button key={page} type="button" onClick={() => navigate(`/${page}`)}>
              {page.replace('-', ' ')}
            </button>
          ),
        )}
        <button type="button" onClick={() => navigate('/account')}>
          Grown-up area
        </button>
      </nav>
    </footer>
  );
}
